import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import { FiChevronLeft, FiChevronRight, FiStar } from 'react-icons/fi';

const testimonials = [
    {
        name: "Ron D.",
        role: "Colorado",
        image: "https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?auto=format&fit=crop&q=80&w=150",
        content: "With Tom's mentorship, I was able to purchase 4 homes in two months. One was a duplex and added exactly $4000 in cash flow to my portfolio!",
        rating: 5
    },
    {
        name: "Eric",
        role: "Mentorship Student",
        image: "https://flipanythingusa.com/wp-content/uploads/2025/11/Tom_Mckay_2-2.jpg",
        content: "I recommend Tom's Mentorship. I have followed him off and on for 3 years. Tom's mentorship has been instrumental in my success buying two properties and when I needed direct help from Tom with my deal Tom was there.",
        rating: 5
    },
    {
        name: "Pat C.",
        role: "Mentorship Student",
        image: "https://images.unsplash.com/photo-1500648767791-00dcc994a43e?auto=format&fit=crop&q=80&w=150",
        content: "I've read several RE books and listened to even more podcasts and Tom is the best, most simple gameplan.",
        rating: 5
    },
    {
        name: "Jaspaul G.",
        role: "Mentorship Student",
        image: "https://images.unsplash.com/photo-1506794778202-cad84cf45f1d?auto=format&fit=crop&q=80&w=150",
        content: "Tom's input has been about as practical as you can get. The information provided is not speculation from some armchair investor, it is hard earned. I would recommend this mentorship to anyone interested in pursuing real estate investment.",
        rating: 5
    },
    {
        name: "Shane & DeeDee",
        role: "Colorado",
        image: "https://images.unsplash.com/photo-1522529599102-193c0d76b5b6?auto=format&fit=crop&q=80&w=150",
        content: "How Shane and Dee Dee made over $350,000 with condos and houses following Tom's proven mentorship system.",
        rating: 5
    }
];

const TestimonialsCarousel = () => {
    const [currentIndex, setCurrentIndex] = useState(0);
    const [direction, setDirection] = useState(0);

    const nextSlide = () => {
        setDirection(1);
        setCurrentIndex((prev) => (prev + 1) % testimonials.length);
    };

    const prevSlide = () => {
        setDirection(-1);
        setCurrentIndex((prev) => (prev - 1 + testimonials.length) % testimonials.length);
    };

    // Auto-advance
    useEffect(() => {
        const timer = setInterval(nextSlide, 6000);
        return () => clearInterval(timer);
    }, []);

    const variants = {
        enter: (direction) => ({
            x: direction > 0 ? 100 : -100,
            opacity: 0
        }),
        center: {
            x: 0,
            opacity: 1
        },
        exit: (direction) => ({
            x: direction > 0 ? -100 : 100,
            opacity: 0
        })
    };

    const current = testimonials[currentIndex];

    return (
        <section className="py-20 bg-slate-50 dark:bg-slate-900/50 overflow-hidden">
            <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
                {/* Header */}
                <div className="text-center mb-16">
                    <h2 className="font-serif text-3xl md:text-4xl font-bold text-slate-900 dark:text-white mb-4">
                        What Students Are Saying
                    </h2>
                    <p className="text-slate-500 dark:text-slate-400">
                        Real results from real estate investors following Tom's proven mentorship.
                    </p>
                </div>

                {/* Carousel */}
                <div className="relative">
                    {/* Main Content */}
                    <div className="relative min-h-[300px] flex items-center justify-center">
                        <AnimatePresence initial={false} custom={direction} mode="wait">
                            <motion.div
                                key={currentIndex}
                                custom={direction}
                                variants={variants}
                                initial="enter"
                                animate="center"
                                exit="exit"
                                transition={{ duration: 0.4, ease: "easeInOut" }}
                                className="absolute w-full"
                            >
                                <div className="bg-white dark:bg-slate-800 rounded-3xl p-8 md:p-12 shadow-xl border border-slate-100 dark:border-slate-700">
                                    {/* Stars */}
                                    <div className="flex items-center justify-center gap-1 mb-8">
                                        {[...Array(current.rating)].map((_, i) => (
                                            <SafeIcon
                                                key={i}
                                                icon={FiStar}
                                                className="w-5 h-5 text-amber-400 fill-current"
                                            />
                                        ))}
                                    </div>

                                    {/* Quote */}
                                    <blockquote className="text-xl md:text-2xl text-slate-700 dark:text-slate-200 text-center leading-relaxed mb-8 font-light">
                                        "{current.content}"
                                    </blockquote>

                                    {/* Author */}
                                    <div className="flex items-center justify-center gap-4">
                                        <img
                                            src={current.image}
                                            alt={current.name}
                                            className="w-14 h-14 rounded-full object-cover ring-4 ring-slate-100 dark:ring-slate-700"
                                        />
                                        <div className="text-left">
                                            <p className="font-bold text-slate-900 dark:text-white">
                                                {current.name}
                                            </p>
                                            <p className="text-sm text-slate-500 dark:text-slate-400">
                                                {current.role}
                                            </p>
                                        </div>
                                    </div>
                                </div>
                            </motion.div>
                        </AnimatePresence>
                    </div>

                    {/* Navigation Arrows */}
                    <div className="flex items-center justify-center gap-4 mt-8">
                        <button
                            onClick={prevSlide}
                            className="p-3 rounded-full bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 text-slate-600 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-700 hover:text-slate-900 dark:hover:text-white transition-all shadow-sm"
                        >
                            <SafeIcon icon={FiChevronLeft} className="w-5 h-5" />
                        </button>

                        {/* Dots */}
                        <div className="flex items-center gap-2">
                            {testimonials.map((_, idx) => (
                                <button
                                    key={idx}
                                    onClick={() => {
                                        setDirection(idx > currentIndex ? 1 : -1);
                                        setCurrentIndex(idx);
                                    }}
                                    className={`w-2 h-2 rounded-full transition-all ${idx === currentIndex
                                        ? 'w-8 bg-emerald-500'
                                        : 'bg-slate-300 dark:bg-slate-600 hover:bg-slate-400'
                                        }`}
                                />
                            ))}
                        </div>

                        <button
                            onClick={nextSlide}
                            className="p-3 rounded-full bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 text-slate-600 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-700 hover:text-slate-900 dark:hover:text-white transition-all shadow-sm"
                        >
                            <SafeIcon icon={FiChevronRight} className="w-5 h-5" />
                        </button>
                    </div>

                    {/* Disclaimer */}
                    <p className="text-center text-xs text-slate-400 mt-8">
                        Individual results vary. Real estate investing involves risk, and no specific outcome or level of profit can be guaranteed.
                    </p>
                </div>
            </div>
        </section>
    );
};

export default TestimonialsCarousel;
